/**
 * UpgradeModal — Popup de limitation premium
 *
 * S'affiche quand un utilisateur atteint la limite de son tier actuel.
 * Propose une ou plusieurs offres d'upgrade selon le tier actuel :
 *   - free    → Basic, Premium, Gold
 *   - basic   → Premium, Gold
 *   - premium → Gold (ou avertissement si limite déjà Gold)
 *   - gold    → pas de popup (illimité/infini par défaut)
 *
 * Props :
 *   visible     : boolean
 *   onClose     : () => void
 *   currentTier : 'free' | 'basic' | 'premium' | 'gold'
 *   feature     : 'wardrobe' | 'ai' | 'looks' | 'travel'
 *   usage       : { current, limit }
 *   onSubscribe : (planId) => void    // appelle handleSubscribe du parent
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet,
  Platform,
} from 'react-native';
import AppModal from './AppModal';
import { showAppAlert } from './AppDialog';
import PremiumModal from './PremiumModal';
import { TIERS } from '../services/PremiumService';
import { startCheckout, startAddonCheckout, startWardrobeSubscriptionCheckout } from '../services/StripeService';
import { ADDONS as ADDONS_FRONT } from '../config/addonsConfig';
import { formatNextDailyReset } from '../utils/resetTimeLocal';
import { openLegalUrl } from '../utils/openLegalUrl';
import { getAddonsForFeature, formatAddonLine } from '../config/addonsConfig';
import { useI18n } from '../i18n';
import { useAuth } from '../context/AuthContext';
// v1.12.493 — réutilise formatRenewalTime de ProfileScreen pour le format date homogène
import { formatRenewalTime } from '../screens/ProfileScreen';

const TIER_ORDER = ['free', 'basic', 'premium', 'gold'];

// v1.12.378 — Messages EN parallèles (pour les users en langue anglaise)
const FEATURE_MESSAGES_EN = {
  wardrobe: {
    icon: '🧥',
    title: 'Wardrobe full',
    desc: (tier, limit) => `You've reached the ${limit}-item limit of your ${tier.label} plan.`,
  },
  ai: {
    icon: '🤖',
    title: 'AI quota reached',
    desc: (tier, limit, current) => {
      const used = (current !== undefined && current !== null) ? current : limit;
      return `You've used ${used}/${limit} AI analyses today (${tier.label} plan).\n\n🕛 Counter resets at midnight.\n\nUpgrade to enjoy more analyses right now.`;
    },
  },
  looks: {
    icon: '✨',
    title: 'Daily looks reached',
    desc: (tier, limit) => `${limit} looks generated today (${tier.label} plan).`,
  },
  travel: {
    icon: '✈️',
    title: 'Travel Mode',
    desc: () => `Travel Mode is included with Basic, Premium and Gold plans. Choose a plan below to unlock this feature.`,
  },
  trips: {
    icon: '✈️',
    title: 'Travel Mode',
    desc: (tier, limit) => {
      if (!limit || limit === 0) {
        return `Travel Mode is included with Basic, Premium and Gold plans. Choose a plan below to unlock this feature.`;
      }
      return `You've reached the ${limit}-trip limit of your ${tier.label} plan. Upgrade to create more trips.`;
    },
  },
  cashback: {
    icon: '✨',
    title: 'Boost your Mytenue Credits',
    desc: (tier) => `Upgrade to earn more Mytenue Credits on every purchase — usable in-app for renewals, upgrades and add-ons (current plan: ${tier.label}).`,
  },
  tryon: {
    icon: '👗',
    title: 'Virtual try-on',
    desc: (tier, limit, current) => {
      if (tier?.id === 'free' || !limit) {
        return `Virtual try-on and Flat-Lay aren't included in the free plan.\n\nBuy a Try-On pack below, or upgrade to Basic, Premium or Gold to enjoy them.`;
      }
      const used = (current !== undefined && current !== null) ? current : limit;
      return `You've used ${used}/${limit} try-ons this month (${tier.label} plan).\n\n🗓️ Counter resets on the 1st of next month.\n\nUpgrade to try more outfits on AI mannequins right now.`;
    },
  },
  reactivate: {
    icon: '🔄',
    title: 'Reactivate your subscription',
    desc: () => `Your subscription is cancelled. Choose below which plan you want to reactivate — Basic, Premium or Gold — to instantly regain your features.`,
  },
};

const FEATURE_MESSAGES = {
  wardrobe: {
    icon: '🧥',
    title: 'Dressing plein',
    desc: (tier, limit) => `Vous avez atteint la limite de ${limit} articles de votre plan ${tier.label}.`,
  },
  ai: {
    icon: '🤖',
    title: 'Quota IA atteint',
    desc: (tier, limit, current) => {
      const used = (current !== undefined && current !== null) ? current : limit;
      return `Vous avez utilisé ${used}/${limit} analyses IA aujourd'hui (plan ${tier.label}).\n\n🕛 Compteur remis à zéro à minuit.\n\nPassez à un plan supérieur pour profiter de plus d'analyses dès maintenant.`;
    },
  },
  looks: {
    icon: '✨',
    title: 'Looks quotidiens atteints',
    desc: (tier, limit) => `${limit} looks générés aujourd'hui (plan ${tier.label}).`,
  },
  travel: {
    icon: '✈️',
    title: 'Module Voyage',
    desc: () => `Le module Voyage est inclus dans les abonnements Basic, Premium et Gold. Choisissez un plan ci-dessous pour débloquer cette fonctionnalité.`,
  },
  trips: {
    icon: '✈️',
    title: 'Module Voyage',
    desc: (tier, limit, current) => {
      if (!limit || limit === 0) {
        return `Le module Voyage est inclus dans les abonnements Basic, Premium et Gold. Choisissez un plan ci-dessous pour débloquer cette fonctionnalité.`;
      }
      return `Vous avez atteint la limite de ${limit} voyages de votre plan ${tier.label}. Passez à un plan supérieur pour créer plus de voyages.`;
    },
  },
  // ═══ v1.8.5 : feature cashback (depuis banner Profil) — v1.12.845 rebrandé "Crédit Mytenue" ═══
  cashback: {
    icon: '✨',
    title: 'Boostez votre Crédit Mytenue',
    desc: (tier) => `Passez à un plan supérieur pour gagner plus de Crédit Mytenue sur chaque achat — utilisable dans l'app pour renouveler, upgrader ou acheter des add-ons (plan actuel : ${tier.label}).`,
  },
  // ═══ v1.10.0 : feature tryon (Try-on virtuel sur mannequin) ═══
  // v1.12.122 : free n'a plus acces -> message different (lock vs quota).
  tryon: {
    icon: '👗',
    title: 'Essai virtuel',
    desc: (tier, limit, current) => {
      // Free : pas d'acces du tout
      if (tier?.id === 'free' || !limit) {
        return `L'essayage virtuel et le Flat-Lay ne sont pas inclus dans le plan gratuit.\n\nAchetez un pack d'essais ci-dessous, ou passez à Basic, Premium ou Gold pour en profiter.`;
      }
      // Tier paye : quota epuise -> proposer upgrade
      const used = (current !== undefined && current !== null) ? current : limit;
      return `Vous avez utilisé ${used}/${limit} essais ce mois (plan ${tier.label}).\n\n🗓️ Compteur réinitialisé le 1er du mois prochain.\n\nPassez à un plan supérieur pour essayer plus de tenues sur les mannequins IA dès maintenant.`;
    },
  },
  // ═══ v1.8.10 : feature reactivate (abonnement annulé qui veut se ré-abonner) ═══
  reactivate: {
    icon: '🔄',
    title: 'Réactivez votre abonnement',
    desc: (tier) => `Votre abonnement est annulé. Choisissez ci-dessous le plan que vous souhaitez réactiver — Basic, Premium ou Gold — pour retrouver immédiatement vos fonctionnalités.`,
  },
};

// Mapping tier → offre spécifique à montrer quand on est sur ce tier
function getUpgradeOptions(currentTier, feature) {
  const idx = TIER_ORDER.indexOf(currentTier);
  // ═══ v1.8.10 : pour "reactivate", proposer TOUS les plans payants ═══
  // (l'user est "cancelled" → il peut ré-acheter n'importe quelle formule)
  if (feature === 'reactivate') {
    return ['basic', 'premium', 'gold'];
  }
  // Pour "travel" et "trips" : afficher tous les plans qui incluent Voyage
  // (Basic, Premium, Gold) depuis le tier courant
  if (feature === 'travel' || feature === 'trips') {
    // Basic, Premium, Gold incluent tous Voyage
    const travelTiers = ['basic', 'premium', 'gold'];
    return travelTiers.filter(t => TIER_ORDER.indexOf(t) > idx);
  }
  // Pour les autres features : tous les tiers supérieurs
  return TIER_ORDER.slice(idx + 1);
}

export default function UpgradeModal({ visible, onClose, currentTier = 'free', feature = 'wardrobe', usage, onSubscribe }) {
  // v1.12.634 — Stabilisation onClose via ref (voir [[appmodal-callback-stable]])
  // Sinon AppModal cleanup → history.back → modal disparaît au moindre re-render
  // (scroll, focus, etc.). Bug reporté sur Wardrobe, LooksAI, Catalogue.
  const _onCloseRef = useRef(onClose);
  useEffect(() => { _onCloseRef.current = onClose; }, [onClose]);
  const stableOnClose = useCallback(() => { _onCloseRef.current?.(); }, []);
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  // v1.12.591 — état pour ouvrir PremiumModal (tous les plans + toggle mensuel/annuel)
  const [showAllPlansModal, setShowAllPlansModal] = useState(false);
  const { lang } = useI18n();
  const isFR = !lang || lang.startsWith('fr');
  const { reloadProfile } = useAuth() || {};

  if (!visible) return null;

  // v1.12.654 — Le block est maintenant sur les AUTO-triggers (events +
  // guardCheck auto dans usePremiumGuard). Si on arrive ici avec visible=true,
  // c'est une ouverture LÉGITIME (manuelle via Profile/Boutique, ou auto qui
  // a échappé au filtre). On affiche normalement.

  // v1.12.378 — sélection FR/EN du dictionnaire de messages
  const MESSAGES = isFR ? FEATURE_MESSAGES : FEATURE_MESSAGES_EN;
  const msg = MESSAGES[feature] || MESSAGES.wardrobe;
  const currentTierData = TIERS[currentTier] || TIERS.free;
  const upgradeTiers = getUpgradeOptions(currentTier, feature);

  // v1.12.119 — % de réduction de l'annuel (calculé sur premium = référence)
  // priceYearlyMonth est le prix mensualisé d'un abonnement annuel ; on
  // compare au priceMonthly pour montrer l'économie.
  const refTier = TIERS.premium || TIERS.basic;
  const yearlyDiscountPct = (refTier?.priceMonthly && refTier?.priceYearlyMonth)
    ? Math.round((1 - refTier.priceYearlyMonth / refTier.priceMonthly) * 100)
    : 20;

  // ═══ Bascule en branche "addons-only" pour TOUTES les features avec addons ═══
  // v1.12.588 — Tous tiers voient les addons (consumable + subscription) plutôt
  // que l'upgrade tier dans le paywall :
  //   - tryon : packs +3/+10/+25 Try-Ons
  //   - ai/looks : packs Boost Shopping +50 / Boost Saison +200
  //   - wardrobe : Boost +50 + (locked Free) Plus/Max
  //   - wardrobe_and_ai : combo
  // v1.12.598 — 'looks' RETIRÉ : aucun addon ne débloque la génération de Looks IA
  // (les addons scans = analyses d'articles, pas génération). Seul l'upgrade vers
  // Premium/Gold débloque les Looks IA illimités → tomber directement dans la
  // branche tier-upgrade qui affiche les plans Basic/Premium/Gold.
  const ADDON_FEATURES = ['wardrobe', 'wardrobe_and_ai', 'tryon', 'ai'];
  const useAddonsBranch = ADDON_FEATURES.includes(feature);

  // v1.12.591 — handlePick déclaré AVANT la branche addons pour qu'elle puisse
  // y accéder (le const n'est pas hoisted, sinon "handlePick is not defined").
  const handlePick = (tierId) => {
    const planId = billingPeriod === 'yearly' ? `${tierId}_yearly` : tierId;
    const targetPeriod = billingPeriod === 'yearly' ? 'yearly' : 'monthly';
    onClose();
    if (Platform.OS === 'web') {
      setTimeout(async () => {
        try {
          // v1.12.658 — Si user déjà abonné: change-subscription
          // Sinon (free): Checkout normal
          const hasActiveSub = currentTier && currentTier !== 'free';
          if (hasActiveSub) {
            // v1.12.661/662 — Confirmation avant change avec message spécifique
            const tierLabel = tierId.charAt(0).toUpperCase() + tierId.slice(1);
            const periodLabel = targetPeriod === 'yearly' ? (isFR ? 'annuel' : 'yearly') : (isFR ? 'mensuel' : 'monthly');
            const currentLabel = (currentTier || 'free').charAt(0).toUpperCase() + (currentTier || 'free').slice(1);
            const isMonthlyToYearlySame = currentTier === tierId && targetPeriod === 'yearly';
            const isYearlyToMonthlySame = currentTier === tierId && targetPeriod === 'monthly';
            const YEARLY_PRICES = { basic: 59.90, premium: 99.90, gold: 169.90 };
            let confirmMsg;
            if (isMonthlyToYearlySame && YEARLY_PRICES[tierId]) {
              confirmMsg = isFR
                ? `Passer ${tierLabel} en annuel :\n\n• Vous payez ${YEARLY_PRICES[tierId]} € maintenant pour 1 an.\n• Vos jours restants de l'abonnement mensuel sont préservés.\n• L'annuel commencera à la fin de votre période mensuelle.\n• Aucun jour perdu.`
                : `Switch ${tierLabel} to yearly:\n\n• Pay ${YEARLY_PRICES[tierId]} € now for 1 year.\n• Your remaining monthly days are preserved.\n• Yearly starts at the end of your monthly period.\n• No days lost.`;
            } else if (isYearlyToMonthlySame) {
              confirmMsg = isFR
                ? `Passer ${tierLabel} en mensuel :\n\n• Vous gardez votre abonnement annuel jusqu'à expiration.\n• Aucun remboursement.\n• Bascule automatique en mensuel à la fin de l'année.\n• Aucun prélèvement immédiat.`
                : `Switch ${tierLabel} to monthly:\n\n• Keep yearly until expiration.\n• No refund.\n• Automatic switch to monthly at year end.\n• No immediate charge.`;
            } else {
              confirmMsg = isFR
                ? `Passer de ${currentLabel} à ${tierLabel} ${periodLabel} ?\n\nProrata facturé si upgrade, ou bascule fin de période si downgrade.`
                : `Switch from ${currentLabel} to ${tierLabel} ${periodLabel} ?\n\nProration billed for upgrade, or applied at period end for downgrade.`;
            }
            const confirmIdx = await showAppAlert(
              isFR ? 'Confirmer le changement' : 'Confirm change',
              confirmMsg,
              [
                { text: isFR ? 'Annuler' : 'Cancel', style: 'cancel' },
                { text: isFR ? 'Confirmer' : 'Confirm', style: 'default' },
              ]
            );
            if (confirmIdx !== 1) return; // cancelled
            const { changeSubscription } = require('../services/StripeService');
            const { showProcessing, hideProcessing } = require('./ProcessingOverlay');
            try {
              showProcessing(
                isFR ? 'Paiement en cours…' : 'Processing payment…',
                isFR ? `Finalisation de votre abonnement ${tierId} ${targetPeriod === 'yearly' ? 'annuel' : 'mensuel'}.` : `Finalizing ${tierId} ${targetPeriod}.`
              );
              const result = await changeSubscription(tierId, targetPeriod);
              hideProcessing();
              if (result?.mode === 'updated') {
                showAppAlert(
                  isFR ? 'Abonnement modifié' : 'Subscription updated',
                  isFR ? `Votre nouveau plan ${tierId} ${targetPeriod === 'yearly' ? 'annuel' : 'mensuel'} est actif immédiatement.`
                       : `Your new ${tierId} ${targetPeriod} plan is active immediately.`
                );
                if (reloadProfile) {
                  [1000, 3000, 6000, 12000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
                }
              } else if (result?.mode === 'prepaid_yearly') {
                const effectiveDate = result.effectiveAt ? new Date(result.effectiveAt).toLocaleDateString(isFR ? 'fr-FR' : 'en-US') : '';
                const renewDate = result.nextRenewalAt ? new Date(result.nextRenewalAt).toLocaleDateString(isFR ? 'fr-FR' : 'en-US') : '';
                showAppAlert(
                  isFR ? '✓ Abonnement annuel confirmé' : '✓ Yearly subscription confirmed',
                  isFR ? `Paiement de ${result.amountPaid} € reçu.\n\nVotre abonnement annuel commencera le ${effectiveDate}.\nProchain renouvellement : ${renewDate}.`
                       : `Payment of ${result.amountPaid} € received.\n\nYearly starts on ${effectiveDate}. Renewal: ${renewDate}.`
                );
                if (reloadProfile) {
                  [1000, 3000, 6000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
                }
              } else if (result?.mode === 'scheduled') {
                const date = result.effectiveAt ? new Date(result.effectiveAt).toLocaleDateString(isFR ? 'fr-FR' : 'en-US') : '';
                showAppAlert(
                  isFR ? 'Changement programmé' : 'Change scheduled',
                  isFR ? `Votre plan ${tierId} ${targetPeriod === 'yearly' ? 'annuel' : 'mensuel'} sera actif le ${date}.`
                       : `Your ${tierId} ${targetPeriod} plan will be active on ${date}.`
                );
                if (reloadProfile) {
                  [1000, 3000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
                }
              }
              return;
            } catch (e) {
              hideProcessing();
              const code = e?.code || '';
              const msg = e?.message || '';
              if (code === 'no_active_subscription' || msg.includes('no_active_subscription') || msg.includes('Use create-checkout-session') || msg.includes('does not exist')) {
                console.warn('[UpgradeModal] changeSubscription failed → fallback Checkout:', msg);
                await startCheckout(tierId, targetPeriod);
                return;
              }
              if (code === 'already_on_this_plan' || msg.includes('already_on_this_plan')) {
                showAppAlert(
                  isFR ? 'Plan identique' : 'Same plan',
                  isFR ? `Vous êtes déjà abonné à ${tierLabel} ${periodLabel}.`
                       : `You're already on ${tierLabel} ${periodLabel}.`
                );
                return;
              }
              if (code === 'already_scheduled_to_this_plan' || msg.includes('already_scheduled_to_this_plan')) {
                showAppAlert(
                  isFR ? 'Changement déjà programmé' : 'Change already scheduled',
                  isFR ? `Un passage à ${tierLabel} ${periodLabel} est déjà programmé.`
                       : `A switch to ${tierLabel} ${periodLabel} is already scheduled.`
                );
                return;
              }
              // v1.12.670 — Conflit pending → renvoie vers Profil
              if (code === 'pending_change_conflict' || msg.includes('pending_change_conflict')) {
                showAppAlert(
                  isFR ? 'Changement en attente' : 'Pending change',
                  isFR ? 'Vous avez déjà un changement d\'abonnement programmé. Allez dans Profil pour l\'annuler avant d\'en faire un nouveau.'
                       : 'You have a pending subscription change. Go to Profile to cancel it before making a new one.'
                );
                return;
              }
              throw e;
            }
          }
          try {
            await startCheckout(tierId, targetPeriod);
          } catch (e) {
            // v1.12.672 — Fallback auto vers change-sub si user en fait abonné
            const code = e?.code || '';
            if (code === 'already_subscribed' || (e?.message || '').includes('already_subscribed')) {
              console.warn('[UpgradeModal] startCheckout → already_subscribed, fallback changeSubscription');
              const { changeSubscription } = require('../services/StripeService');
              const result = await changeSubscription(tierId, targetPeriod);
              if (result?.mode === 'updated') {
                showAppAlert(isFR ? 'Abonnement modifié' : 'Subscription updated', isFR ? `Plan ${tierId} ${targetPeriod} actif immédiatement.` : `${tierId} ${targetPeriod} active immediately.`);
              } else if (result?.mode === 'scheduled') {
                showAppAlert(isFR ? 'Changement programmé' : 'Change scheduled', isFR ? 'Bascule à la fin de la période.' : 'Switch at period end.');
              } else if (result?.mode === 'prepaid_yearly') {
                showAppAlert(isFR ? '✓ Annuel confirmé' : '✓ Yearly confirmed', isFR ? `Paiement de ${result.amountPaid}€ reçu.` : `Payment of ${result.amountPaid}€ received.`);
              }
              if (reloadProfile) [1000, 3000, 6000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
              return;
            }
            throw e;
          }
        } catch (e) {
          showAppAlert(
            isFR ? 'Paiement indisponible' : 'Payment unavailable',
            e?.message || (isFR ? 'Erreur Stripe' : 'Stripe error')
          );
        }
      }, 250);
      return;
    }
    setTimeout(() => onSubscribe && onSubscribe(planId), 250);
  };

  if (upgradeTiers.length === 0 || useAddonsBranch) {
    const isDailyAI = feature === 'ai' || feature === 'looks';
    const isMonthlyTryon = feature === 'tryon';
    const isWardrobeSlot = feature === 'wardrobe';
    const isCombined = feature === 'wardrobe_and_ai'; // v1.12.583
    // v1.12.572 — propose TOUS les packs disponibles pour le type (triés par prix)
    const addons = getAddonsForFeature(feature, currentTier || 'gold');

    const onBuyAddon = async (addonId) => {
      try {
        if (Platform.OS === 'web') {
          // v1.12.601 V5.4 — route vers le bon endpoint selon le type d'addon
          const cfg = ADDONS_FRONT[addonId];
          if (cfg?.consumability === 'subscription') {
            await startWardrobeSubscriptionCheckout(addonId);
          } else {
            await startAddonCheckout(addonId);
          }
          return;
        }
        // v1.12.573 — Mobile via RevenueCat (Phase B.3)
        const { purchaseAddon } = require('../services/IAPService');
        // userId géré par RC.logIn() au login → pas besoin de le repasser ici
        const result = await purchaseAddon(addonId, { id: null });
        if (result?.cancelled) return;
        if (result?.success) {
          // Récupérer le label depuis le mapping
          const { ADDONS } = require('../config/addonsConfig');
          const cfg = ADDONS[addonId];
          const unit = cfg?.type === 'tryons' ? 'Try-Ons'
                    : cfg?.type === 'scans'  ? (isFR ? 'analyses IA' : 'AI scans')
                    : (isFR ? 'slots dressing' : 'wardrobe slots');
          showAppAlert?.(
            isFR ? '✅ Achat confirmé' : '✅ Purchase confirmed',
            isFR ? `Votre achat de +${cfg?.quantity} ${unit} est validé. Le solde sera mis à jour sous peu.`
                  : `Your purchase of +${cfg?.quantity} ${unit} is confirmed. Balance will update shortly.`
          );
          onClose();
        }
      } catch (e) {
        console.warn('[UpgradeModal] addon checkout error:', e?.message);
        // Erreur courante : "Pack introuvable" si RC offering "addons" pas encore créé
        const isNoPackage = /introuvable|not found|no package/i.test(e?.message || '');
        showAppAlert?.(
          isFR ? 'Achat indisponible' : 'Purchase unavailable',
          isNoPackage
            ? (isFR ? 'Cet article sera bientôt disponible à l\'achat. Réessayez plus tard.'
                    : 'This item will be available for purchase soon. Please try again later.')
            : (e?.message || (isFR ? 'Erreur paiement' : 'Payment error'))
        );
      }
    };

    return (
      <>
      {/* v1.12.935 — iOS ne peut pas empiler 2 RNModal : on masque ce modal
          quand PremiumModal (showAllPlansModal) s'ouvre, sinon le bouton
          "upgrader votre abonnement" ne présente rien sur iPhone. */}
      {/* v1.13.92 — disableHistory : le paywall s'ouvre souvent JUSTE APRÈS/PENDANT
          la fermeture d'un autre AppModal (popup de progression d'ajout, etc.). Sur
          web, la fermeture de cet autre modal fait window.history.back(), ce qui pop
          l'entrée history du paywall (posée au-dessus) → popstate → le paywall se
          FERME tout seul ("s'affiche et disparaît"), sur Dressing, Looks AI et clic +.
          En ne poussant PAS d'entrée history, le paywall est immunisé. Il se ferme
          via son X / ses boutons (comportement voulu pour un paywall). */}
      <AppModal visible={!showAllPlansModal} transparent animationType="fade" onRequestClose={stableOnClose} disableHistory>
        <View style={s.overlay}>
          <View style={s.card}>
            {/* v1.12.996 — bouton fermer en haut à droite */}
            <TouchableOpacity style={s.closeX} onPress={onClose} hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }} activeOpacity={0.7}>
              <Text style={s.closeXText}>×</Text>
            </TouchableOpacity>
            <View style={s.headerIconWrap}>
              <Text style={s.headerIcon}>{msg.icon}</Text>
            </View>
            <Text style={s.title}>{isFR ? 'Limite atteinte' : 'Limit reached'}</Text>
            <Text style={s.desc}>
              {(() => {
                // Texte adapté selon la nature du compteur :
                //  - daily AI : reset le lendemain à 00:00 Paris (pas "anniversary")
                //  - monthly tryon : reset à l'anniversaire mensuel
                //  - wardrobe slot : permanent (pas de reset)
                const reset = usage?.nextResetDate || usage?.next_reset_date || usage?.resetDate;
                const renewMsg = reset && isMonthlyTryon ? formatRenewalTime(reset, lang) : null;
                const cleanMsg = renewMsg ? renewMsg.replace('🔄 ', '') : null;
                // v1.12.571 — reset daily exprimé dans le timezone local du user
                // (gère heure d'été/hiver automatiquement via Intl).
                // Le serveur compte par jour calendaire Paris, donc on traduit
                // "minuit Paris" en heure locale : 00:00 pour Paris, 01:00 pour
                // Moscow, 18:00 pour New York, 07:00 (lendemain) pour Tokyo.
                const dailyReset = isDailyAI ? formatNextDailyReset(lang || 'fr-FR') : null;
                // v1.12.580 — V5.1-bis : nom du tier courant pour personnalisation
                // v1.12.935 — le label 'free' est 'Gratuit' en dur (PremiumService) :
                // on le traduit selon la langue (sinon "Gratuit" s'affiche en EN).
                const tierLabel = currentTier === 'free'
                  ? (isFR ? 'Gratuit' : 'Free')
                  : (currentTierData?.label || currentTier || (isFR ? 'Gratuit' : 'Free'));
                // v1.12.583 — V5.1-ter : cas combiné wardrobe + AI atteintes
                if (isCombined) {
                  if (isFR) {
                    return `Pour ajouter un article : vous avez atteint à la fois la limite de votre dressing (${tierLabel}) ET votre limite d'analyses IA quotidienne.\n\nChoisissez un pack pour débloquer les deux d'un coup, ou achetez séparément.`;
                  }
                  return `To add an item: you've reached both your wardrobe limit (${tierLabel}) AND your daily AI analyses limit.\n\nChoose a pack to unlock both at once, or buy them separately.`;
                }
                // v1.12.595 — Message dédié pour Looks AI (limite quotidienne sur Free/Basic)
                const isLooks = feature === 'looks';
                if (isFR) {
                  if (isLooks) {
                    return `Vous avez atteint votre limite quotidienne de looks IA sur ${tierLabel} (${currentTierData?.looks_per_day ?? '?'} par jour).\n\nPour voir tous vos looks IA en clair et en illimité, passez à l'abonnement Premium.`;
                  }
                  if (isDailyAI) {
                    const tzPart = dailyReset?.tzName ? ` ${dailyReset.tzName}` : '';
                    return `Vous avez atteint votre limite quotidienne sur ${tierLabel}. Les compteurs se réinitialisent à ${dailyReset.localTimeStr}${tzPart} (dans ${dailyReset.hoursUntil}h).\n\nPour continuer immédiatement, achetez un pack supplémentaire.`;
                  }
                  if (isMonthlyTryon) {
                    // v1.12.879 — Free n'a pas d'échéance mensuelle (pas d'abonnement)
                    if (currentTier === 'free') {
                      return `Le partage Essai virtuel + Flat-Lay est réservé aux abonnés.\n\nPassez à un abonnement Basic (5 essais/mois) ou Premium (15 essais/mois) pour en profiter.`;
                    }
                    return cleanMsg
                      ? `Vous avez atteint votre limite mensuelle sur ${tierLabel}. ${cleanMsg}.\n\nPour continuer immédiatement, achetez un pack supplémentaire.`
                      : `Vous avez atteint votre limite mensuelle sur ${tierLabel}. Les compteurs se réinitialisent à la prochaine échéance mensuelle.\n\nPour continuer immédiatement, achetez un pack supplémentaire.`;
                  }
                  if (isWardrobeSlot) {
                    // v5 : message wardrobe dépend du tier (Free voit Boost only + upsell vers Basic)
                    if (currentTier === 'free') {
                      return `Cet article est au-delà de la limite de votre dressing Free (20 pièces).\n\n• Boost +50 (1,99 €) débloque 50 slots pour 45 jours.\n• Pour des slots permanents (+100 ou +250), passez à l'abonnement Basic.`;
                    }
                    return `Cet article est au-delà de la limite de votre abonnement ${tierLabel}.\n\nDébloquez plus de slots avec un Boost (45 jours) ou un abonnement Espace Plus / Max (permanent).`;
                  }
                  return `Vous avez atteint la limite incluse dans votre abonnement ${tierLabel}. Achetez un pack supplémentaire pour continuer.`;
                }
                // v1.12.595 — EN version dédiée Looks AI
                if (isLooks) {
                  return `You've reached your daily AI looks limit on ${tierLabel} (${currentTierData?.looks_per_day ?? '?'} per day).\n\nTo see all your AI looks clearly and without limit, upgrade to Premium.`;
                }
                if (isDailyAI) {
                  const tzPart = dailyReset?.tzName ? ` ${dailyReset.tzName}` : '';
                  return `You've reached your daily limit on ${tierLabel}. Counters reset at ${dailyReset.localTimeStr}${tzPart} (in ${dailyReset.hoursUntil}h).\n\nTo continue right now, buy an additional pack.`;
                }
                if (isMonthlyTryon) {
                  // v1.12.879 — Free has no monthly cycle (no subscription)
                  if (currentTier === 'free') {
                    return `Virtual Try-on + Flat-Lay are reserved for subscribers.\n\nUpgrade to Basic (5 tries/month) or Premium (15 tries/month) to use these features.`;
                  }
                  return cleanMsg
                    ? `You've reached your monthly limit on ${tierLabel}. ${cleanMsg}.\n\nTo continue right now, buy an additional pack.`
                    : `You've reached your monthly limit on ${tierLabel}. Counters reset on the next monthly anniversary.\n\nTo continue right now, buy an additional pack.`;
                }
                if (isWardrobeSlot) {
                  if (currentTier === 'free') {
                    return `This item is beyond your Free wardrobe limit (20 items).\n\n• Boost +50 (€1.99) unlocks 50 slots for 45 days.\n• For permanent slots (+100 or +250), upgrade to Basic.`;
                  }
                  return `This item is beyond your ${tierLabel} wardrobe limit.\n\nUnlock more slots with a Boost (45 days) or a Space Plus / Max subscription (permanent).`;
                }
                return `You've reached the limit included in your ${tierLabel} subscription. Buy an additional pack to continue.`;
              })()}
            </Text>
            {/* v1.12.572 — liste des packs disponibles, groupés par catégorie en v5.1-ter */}
            {addons.length > 0 && (() => {
              // v1.12.584 — Groupage des addons en sections pour le case combiné
              // (wardrobe_and_ai) : Dressing d'abord, Analyses IA ensuite.
              const wardrobeGroup = addons.filter(a => a.type === 'wardrobe_boost' || a.type === 'wardrobe_subscription');
              const scansGroup = addons.filter(a => a.type === 'scans');
              const tryonsGroup = addons.filter(a => a.type === 'tryons');
              const renderAddon = (addon) => {
                const isPopular = addon.badge === 'popular';
                const isBest = addon.badge === 'best_value';
                const isLocked = addon._locked === true;
                const line = formatAddonLine(addon, lang);
                const onPressLocked = () => {
                  // v1.12.587 — Free clique sur un Plus/Max → upsell vers Basic
                  // (ferme le modal addons et ouvre le modal upgrade tiers)
                  showAppAlert?.(
                    isFR ? '🔒 Réservé aux abonnés Basic+' : '🔒 Reserved for Basic+ subscribers',
                    isFR
                      ? `Les abonnements Espace Plus et Espace Max nécessitent un abonnement Basic ou supérieur.\n\nLe Boost +50 (1,99 € pour 45 jours) reste disponible pour tous les comptes.`
                      : `Space Plus and Space Max subscriptions require a Basic subscription or higher.\n\nThe Boost +50 (€1.99 for 45 days) remains available for all accounts.`
                  );
                };
                return (
                  <TouchableOpacity
                    key={addon.id}
                    onPress={() => isLocked ? onPressLocked() : onBuyAddon(addon.id)}
                    activeOpacity={0.85}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: isLocked ? '#F0F0F0' : (isBest ? '#D4AF37' : (isPopular ? '#FFF8E1' : '#F7F7F7')),
                      borderWidth: isBest && !isLocked ? 0 : 1,
                      borderColor: isLocked ? '#D0D0D0' : (isPopular ? '#D4AF37' : '#E0E0E0'),
                      borderRadius: 10,
                      paddingVertical: 12,
                      paddingHorizontal: 14,
                      opacity: isLocked ? 0.7 : 1,
                    }}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 14, fontWeight: '600', color: isLocked ? '#888' : (isBest ? '#000' : '#1A1A1A') }}>
                        {isLocked ? '🔒 ' : ''}{line}
                      </Text>
                      {!!addon.ttlDays && (
                        <Text style={{ fontSize: 11, color: isLocked ? '#999' : (isBest ? '#333' : '#888'), marginTop: 2 }}>
                          {isFR ? `Valable ${addon.ttlDays} ${addon.ttlDays > 1 ? 'jours' : 'jour'}` : `Valid for ${addon.ttlDays} day${addon.ttlDays > 1 ? 's' : ''}`}
                        </Text>
                      )}
                      {addon.recurringInterval === 'month' && (
                        <Text style={{ fontSize: 11, color: isLocked ? '#999' : (isBest ? '#333' : '#888'), marginTop: 2 }}>
                          {isFR ? 'Abonnement mensuel · résiliable à tout moment' : 'Monthly subscription · cancel anytime'}
                        </Text>
                      )}
                    </View>
                    {isLocked ? (
                      <View style={{
                        paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12,
                        backgroundColor: '#666', marginLeft: 8,
                      }}>
                        <Text style={{ fontSize: 10, fontWeight: '700', color: '#FFF', letterSpacing: 0.5 }}>
                          {isFR ? 'BASIC+ REQUIS' : 'BASIC+ REQUIRED'}
                        </Text>
                      </View>
                    ) : (isPopular || isBest) ? (
                      <View style={{
                        paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12,
                        backgroundColor: isBest ? '#000' : '#D4AF37',
                        marginLeft: 8,
                      }}>
                        <Text style={{ fontSize: 10, fontWeight: '700', color: '#FFF', letterSpacing: 0.5 }}>
                          {isBest ? (isFR ? 'MEILLEURE OFFRE' : 'BEST VALUE') : (isFR ? 'POPULAIRE' : 'POPULAR')}
                        </Text>
                      </View>
                    ) : null}
                  </TouchableOpacity>
                );
              };
              return (
                <View style={{ width: '100%', gap: 12, marginBottom: 12 }}>
                  {wardrobeGroup.length > 0 && (
                    <View style={{ gap: 8 }}>
                      {(scansGroup.length > 0 || tryonsGroup.length > 0) && (
                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#666', letterSpacing: 0.5, marginBottom: 2 }}>
                          {isFR ? '👔 DRESSING — SLOTS SUPPLÉMENTAIRES' : '👔 WARDROBE — EXTRA SLOTS'}
                        </Text>
                      )}
                      {wardrobeGroup.map(renderAddon)}
                    </View>
                  )}
                  {scansGroup.length > 0 && (
                    <View style={{ gap: 8 }}>
                      {(wardrobeGroup.length > 0 || tryonsGroup.length > 0) && (
                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#666', letterSpacing: 0.5, marginBottom: 2 }}>
                          {isFR ? '✨ ANALYSES IA — SCANS BONUS' : '✨ AI ANALYSES — BONUS SCANS'}
                        </Text>
                      )}
                      {scansGroup.map(renderAddon)}
                    </View>
                  )}
                  {tryonsGroup.length > 0 && (
                    <View style={{ gap: 8 }}>
                      {(wardrobeGroup.length > 0 || scansGroup.length > 0) && (
                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#666', letterSpacing: 0.5, marginBottom: 2 }}>
                          {isFR ? '👗 TRY-ON — ESSAIS VIRTUELS' : '👗 TRY-ON — VIRTUAL FITTINGS'}
                        </Text>
                      )}
                      {tryonsGroup.map(renderAddon)}
                    </View>
                  )}
                </View>
              );
            })()}
            {/* Compat : fragment vide pour ne pas casser le JSX en dessous */}
            {false && addons.length > 0 && (
              <View>
                {addons.map((addon) => {
                  const isPopular = addon.badge === 'popular';
                  const isBest = addon.badge === 'best_value';
                  const line = formatAddonLine(addon, lang);
                  return (
                    <TouchableOpacity
                      key={addon.id}
                      onPress={() => onBuyAddon(addon.id)}
                      activeOpacity={0.85}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: isBest ? '#D4AF37' : (isPopular ? '#FFF8E1' : '#F7F7F7'),
                        borderWidth: isBest ? 0 : 1,
                        borderColor: isPopular ? '#D4AF37' : '#E0E0E0',
                        borderRadius: 10,
                        paddingVertical: 12,
                        paddingHorizontal: 14,
                      }}
                    >
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: 14, fontWeight: '600', color: isBest ? '#000' : '#1A1A1A' }}>
                          {line}
                        </Text>
                        {!!addon.ttlDays && (
                          <Text style={{ fontSize: 11, color: isBest ? '#333' : '#888', marginTop: 2 }}>
                            {isFR ? `Valable ${addon.ttlDays} ${addon.ttlDays > 1 ? 'jours' : 'jour'}` : `Valid for ${addon.ttlDays} day${addon.ttlDays > 1 ? 's' : ''}`}
                          </Text>
                        )}
                      </View>
                      {(isPopular || isBest) && (
                        <View style={{
                          paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12,
                          backgroundColor: isBest ? '#000' : '#D4AF37',
                          marginLeft: 8,
                        }}>
                          <Text style={{ fontSize: 10, fontWeight: '700', color: '#FFF', letterSpacing: 0.5 }}>
                            {isBest ? (isFR ? 'MEILLEURE OFFRE' : 'BEST VALUE') : (isFR ? 'POPULAIRE' : 'POPULAR')}
                          </Text>
                        </View>
                      )}
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}
            {/* v1.12.591 — Lien discret "à partir de X €" qui ouvre le PremiumModal
                (tous les plans supérieurs avec toggle mensuel/annuel + avantages). */}
            {upgradeTiers.length > 0 && (() => {
              // Prix mini parmi tous les tiers éligibles (yearly équivalent mensuel)
              const cheapestEur = Math.min(...upgradeTiers.map(t => {
                const td = TIERS[t];
                return td?.priceYearlyMonth ?? td?.priceMonthly ?? Infinity;
              }));
              const cheapestStr = isFinite(cheapestEur)
                ? cheapestEur.toFixed(2).replace('.', isFR ? ',' : '.')
                : '—';
              return (
                <TouchableOpacity
                  onPress={() => setShowAllPlansModal(true)}
                  activeOpacity={0.7}
                  style={{
                    width: '100%', marginBottom: 10,
                    paddingVertical: 11, paddingHorizontal: 14,
                    borderRadius: 10,
                    backgroundColor: 'transparent',
                    borderWidth: 1, borderColor: '#D4AF37',
                    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                  }}
                >
                  <Text style={{ fontSize: 13, color: '#1A1A1A', flex: 1, fontWeight: '500' }}>
                    {isFR
                      ? `✨ Ou upgradez votre abonnement à partir de ${cheapestStr} €/mois`
                      : `✨ Or upgrade your subscription from ${cheapestStr} €/mo`}
                  </Text>
                  <Text style={{ fontSize: 16, color: '#D4AF37', fontWeight: '700' }}>→</Text>
                </TouchableOpacity>
              );
            })()}
            <TouchableOpacity style={[s.closeBtn, { backgroundColor: 'transparent', borderWidth: 1, borderColor: '#ccc' }]} onPress={onClose} activeOpacity={0.85}>
              <Text style={[s.closeBtnText, { color: '#666' }]}>{isFR ? 'Plus tard' : 'Later'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </AppModal>
      {/* v1.12.591 — PremiumModal pour comparer tous les plans supérieurs */}
      <PremiumModal
        visible={showAllPlansModal}
        onClose={() => setShowAllPlansModal(false)}
        trigger={feature}
        currentTier={currentTier}
        onSubscribe={(planId) => {
          setShowAllPlansModal(false);
          onClose();
          setTimeout(() => onSubscribe && onSubscribe(planId), 250);
        }}
      />
    </>
  );
  }


  return (
    <AppModal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={s.overlay}>
        <View style={s.card}>
          {/* Close button */}
          <TouchableOpacity style={s.closeX} onPress={onClose} hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }} delayPressIn={0}>
            <Text style={s.closeXText}>×</Text>
          </TouchableOpacity>

          {/* Header compact : icone + titre côte à côte */}
          <View style={s.headerRow}>
            <View style={s.headerIconWrap}>
              <Text style={s.headerIcon}>{msg.icon}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.title}>{msg.title}</Text>
              {/* v1.12.379 — BUG REACT : `usage.limit && (...)` rendait `0`
                  directement quand limit=0 (free). Wrap avec ternaire null pour
                  forcer un rendu propre. Plus exclusion des features sans quota. */}
              {(
                !!usage && Number(usage.limit) > 0 && isFinite(usage.limit) &&
                feature !== 'tryon' && feature !== 'travel' && feature !== 'trips'
              ) ? (
                <Text style={s.usageInline}>{usage.current}/{usage.limit}</Text>
              ) : null}
            </View>
          </View>

          <Text style={s.desc} numberOfLines={3}>
            {msg.desc(currentTierData, usage?.limit, usage?.current)}
          </Text>

          {/* Toggle mensuel/annuel */}
          <View style={s.billingToggle}>
            <TouchableOpacity
              style={[s.billingTab, billingPeriod === 'monthly' && s.billingTabActive]}
              onPress={() => setBillingPeriod('monthly')}
              activeOpacity={0.85}
            >
              <Text style={[s.billingTabText, billingPeriod === 'monthly' && s.billingTabTextActive]}>{isFR ? 'Mensuel' : 'Monthly'}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[s.billingTab, billingPeriod === 'yearly' && s.billingTabActive]}
              onPress={() => setBillingPeriod('yearly')}
              activeOpacity={0.85}
            >
              <Text style={[s.billingTabText, billingPeriod === 'yearly' && s.billingTabTextActive]}>
                {isFR ? 'Annuel' : 'Yearly'}
              </Text>
              <View style={s.discountBadge}>
                <Text style={s.discountText}>{isFR ? '2 mois offerts' : '2 months free'}</Text>
              </View>
            </TouchableOpacity>
          </View>

          {/* Upgrade options compactes */}
          <View style={s.tiersList}>
            {upgradeTiers.map(tierId => {
              const t = TIERS[tierId];
              const price = billingPeriod === 'yearly' ? t.priceYearlyMonth : t.priceMonthly;
              const yearlyTotal = billingPeriod === 'yearly' ? t.priceYearly : null;
              const isRecommended = tierId === 'premium' || (currentTier !== 'free' && tierId === upgradeTiers[0]);
              const TIER_META = {
                basic:   { emoji: '⭐', color: '#6C9AC4', bg: '#EEF4FB', border: '#BAD2EC' },
                premium: { emoji: '✨', color: '#B8860B', bg: '#FFF9E6', border: '#F5DEB3' },
                gold:    { emoji: '👑', color: '#8B6914', bg: '#FFF4CC', border: '#D4AF37' },
              };
              const meta = TIER_META[tierId] || TIER_META.basic;
              return (
                <TouchableOpacity
                  key={tierId}
                  style={[s.tierCard, { borderColor: meta.border, backgroundColor: meta.bg }, isRecommended && s.tierCardRecommended]}
                  onPress={() => handlePick(tierId)}
                  activeOpacity={0.85}
                >
                  {isRecommended && (
                    <View style={[s.recommendBadge, { backgroundColor: meta.color }]}>
                      <Text style={s.recommendBadgeText}>{isFR ? 'RECOMMANDÉ' : 'RECOMMENDED'}</Text>
                    </View>
                  )}
                  <View style={s.tierCardLeft}>
                    <Text style={s.tierEmoji}>{meta.emoji}</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={[s.tierName, { color: meta.color }]}>{t.label.toUpperCase()}</Text>
                      <Text style={s.tierSpecs}>
                        {t.wardrobe_items === Infinity ? '∞' : t.wardrobe_items} {isFR ? 'articles' : 'items'} · {t.ai_requests} {isFR ? 'IA/j' : 'AI/day'}
                        {typeof t.tryons_per_month === 'number' && t.tryons_per_month > 0 && (
                          ` · ${t.tryons_per_month === Infinity ? '∞' : t.tryons_per_month} ${isFR ? 'essais/m' : 'try-ons/m'}`
                        )}
                        {/* v1.12.920 — Voyages masqués (feature reportée post-lancement) */}
                      </Text>
                    </View>
                  </View>
                  <View style={s.tierCardRight}>
                    {/* Apple 3.1.2(c) : le MONTANT FACTURÉ est l'élément le plus visible.
                        Annuel → on affiche le total annuel en gros (59,90 €/an) et le
                        mensuel calculé en petit/subordonné (soit 4,99 €/mois). */}
                    {billingPeriod === 'yearly' && yearlyTotal != null ? (
                      <>
                        <Text style={[s.tierPrice, { color: meta.color }]}>{isFR ? `${yearlyTotal.toFixed(2)}€` : `€${yearlyTotal.toFixed(2)}`}</Text>
                        <Text style={s.tierPriceUnit}>{isFR ? '/an' : '/year'}</Text>
                        <Text style={s.tierPriceYearly}>
                          {isFR ? `soit ${price.toFixed(2)}€/mois` : `i.e. €${price.toFixed(2)}/mo`}
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text style={[s.tierPrice, { color: meta.color }]}>{isFR ? `${price.toFixed(2)}€` : `€${price.toFixed(2)}`}</Text>
                        <Text style={s.tierPriceUnit}>{isFR ? '/mois' : '/month'}</Text>
                      </>
                    )}
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* v1.12.934 — Apple Guideline 3.1.2(c) : disclosure auto-renouvellement
              + liens EULA/Privacy DANS le flux d'achat (FR+EN). */}
          <Text style={s.legalDisclosure}>
            {isFR
              ? "Abonnement auto-renouvelable. Le paiement est débité lors de la confirmation. Le renouvellement est automatique à la même période et au même tarif, sauf annulation au moins 24 h avant la fin de la période en cours. Gérez ou résiliez dans les réglages de votre compte Apple ID."
              : "Auto-renewable subscription. Payment is charged upon confirmation of purchase. It renews automatically at the same price and duration unless canceled at least 24 hours before the end of the current period. Manage or cancel anytime in your Apple ID account settings."}
          </Text>
          <View style={s.legalLinksRow}>
            <Text onPress={() => openLegalUrl('terms', isFR ? 'fr' : 'en')} style={s.legalLink}>
              {isFR ? "Conditions d'utilisation (EULA)" : 'Terms of Use (EULA)'}
            </Text>
            <Text style={s.legalSep}>{'  ·  '}</Text>
            <Text onPress={() => openLegalUrl('privacy', isFR ? 'fr' : 'en')} style={s.legalLink}>
              {isFR ? 'Politique de confidentialité' : 'Privacy Policy'}
            </Text>
          </View>

          {/* "Plus tard" — directement en dessous, pas de ScrollView */}
          <TouchableOpacity
            onPress={onClose}
            style={s.laterBtn}
            activeOpacity={0.5}
            hitSlop={{ top: 8, bottom: 8, left: 24, right: 24 }}
            delayPressIn={0}
          >
            <Text style={s.laterBtnText}>{isFR ? 'Plus tard' : 'Later'}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </AppModal>
  );
}

const s = StyleSheet.create({
  overlay: {
    flex: 1,
    // v1.12.994 — fond assombri sur natif (RN Modal transparent ne dim pas) pour
    // détacher la popup du fond blanc de l'app. Sur web AppModal a déjà un backdrop.
    backgroundColor: Platform.OS === 'web' ? 'transparent' : 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    padding: 18,
    position: 'relative',
    // v1.12.994 — bordure + ombre pour bien distinguer la popup
    borderWidth: 1,
    borderColor: '#E8DCC2',
    shadowColor: '#000', shadowOffset: { width: 0, height: 10 }, shadowOpacity: 0.20, shadowRadius: 24, elevation: 14,
  },
  closeX: {
    position: 'absolute',
    top: 10, right: 10,
    width: 28, height: 28, borderRadius: 14,
    backgroundColor: '#F5F5F5',
    alignItems: 'center', justifyContent: 'center',
    zIndex: 2,
  },
  closeXText: { fontSize: 16, color: '#666', fontWeight: '300', lineHeight: 18 },

  // v1.12.934 — bloc légal abonnement (Guideline 3.1.2c)
  legalDisclosure: { fontSize: 10, color: '#9E9E9E', lineHeight: 14, textAlign: 'center', marginTop: 12, paddingHorizontal: 4 },
  legalLinksRow: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginTop: 6 },
  legalLink: { fontSize: 11, color: '#666', textDecorationLine: 'underline' },
  legalSep: { fontSize: 11, color: '#BDBDBD' },

  // v1.12.119 — header compact en row (icône + titre côte à côte)
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
    paddingRight: 28, // évite le close X
  },
  headerIconWrap: {
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: '#FFF9E6',
    borderWidth: 1.5, borderColor: '#F5DEB3',
    alignItems: 'center', justifyContent: 'center',
  },
  headerIcon: { fontSize: 22 },

  title: {
    fontSize: 17, fontWeight: '600', color: '#1A1A1A',
    letterSpacing: 0.2,
  },
  usageInline: {
    fontSize: 11, fontWeight: '700', color: '#E65100',
    letterSpacing: 0.3, marginTop: 2,
  },
  desc: {
    fontSize: 12, color: '#666',
    lineHeight: 17, marginBottom: 12,
  },

  billingToggle: {
    flexDirection: 'row',
    backgroundColor: '#F5F3EE',
    borderRadius: 100,
    padding: 3,
    alignSelf: 'center',
    marginBottom: 12,
  },
  billingTab: {
    paddingHorizontal: 16, paddingVertical: 6,
    borderRadius: 100,
    flexDirection: 'row', alignItems: 'center', gap: 5,
  },
  billingTabActive: { backgroundColor: '#1A1A1A' },
  billingTabText: {
    fontSize: 11, fontWeight: '600', color: '#888', letterSpacing: 0.3,
  },
  billingTabTextActive: { color: '#D4AF72' },
  discountBadge: {
    backgroundColor: '#22C55E',
    borderRadius: 8,
    paddingHorizontal: 6, paddingVertical: 1,
  },
  discountText: { fontSize: 9, color: '#FFFFFF', fontWeight: '800', letterSpacing: 0.3 },

  tiersList: { gap: 8, marginBottom: 4 },
  tierCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1.5,
    borderRadius: 12,
    paddingHorizontal: 12, paddingVertical: 10,
    position: 'relative',
  },
  tierCardRecommended: { borderWidth: 2.2 },
  recommendBadge: {
    position: 'absolute',
    top: -8, right: 10,
    paddingHorizontal: 8, paddingVertical: 2,
    borderRadius: 8,
  },
  recommendBadgeText: {
    fontSize: 8, color: '#FFF', fontWeight: '700', letterSpacing: 0.8,
  },
  tierCardLeft: {
    flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1,
  },
  tierEmoji: { fontSize: 22 },
  tierName: {
    fontSize: 12, fontWeight: '700', letterSpacing: 1.2, marginBottom: 1,
  },
  tierSpecs: {
    fontSize: 10, color: '#666', letterSpacing: 0.2,
  },
  tierCardRight: {
    alignItems: 'flex-end',
  },
  tierPrice: { fontSize: 18, fontWeight: '700', lineHeight: 20 },
  tierPriceUnit: { fontSize: 9, color: '#999', marginTop: -2 },
  tierPriceYearly: { fontSize: 9, color: '#888', marginTop: 1, fontStyle: 'italic' },

  laterBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    marginTop: 8,
  },
  laterBtnText: {
    fontSize: 12, color: '#999',
    letterSpacing: 0.8, textTransform: 'uppercase',
    fontWeight: '500',
  },

  // Mode "déjà Gold"
  closeBtn: {
    backgroundColor: '#1A1A1A',
    borderRadius: 100,
    paddingHorizontal: 32, paddingVertical: 14,
    alignSelf: 'center',
    marginTop: 8,
  },
  closeBtnText: {
    color: '#D4AF72', fontWeight: '700', letterSpacing: 1.5, fontSize: 13,
  },
});
